<template>
	<div class="website-page-agent-assistant">
		<div v-if="!agentBaseUrl" class="caption error--text pa-2">
			Set AGENT_ORCHESTRATOR_URL in config to enable the assistant.
		</div>
		<template v-else>
			<div class="pane-header">
				<div class="header-content">
					<div class="header-left">
						<img
							class="header-logo"
							:src="logoWaifinderSrc"
							alt=""
							height="28"
						>
					</div>
					<div class="header-right">
						<v-tooltip bottom>
							<template #activator="{ on, attrs }">
								<v-btn
									v-bind="attrs"
									icon
									small
									text
									class="header-action-btn"
									:disabled="isLoading"
									v-on="on"
									@click="resetChat"
								>
									<v-icon small>
										fa-refresh
									</v-icon>
								</v-btn>
							</template>
							<span>Reset chat</span>
						</v-tooltip>
					</div>
				</div>
			</div>

			<div ref="messagesEl" class="messages-container">
				<div
					v-for="(m, idx) in messages"
					:key="`msg-${idx}`"
					class="message-wrapper"
					:class="m.role === 'user' ? 'user-message' : 'bot-message'"
				>
					<div v-if="m.role === 'assistant'" class="message-icon">
						<img
							class="wayfinder-symbol wayfinder-symbol--message"
							:src="waifinderSymbolSrc"
							alt=""
						>
					</div>
					<template v-if="m.role === 'user'">
						<div class="message-bubble">
							<div class="message-content">
								<span v-html="formatMessage(m.content)" />
							</div>
						</div>
						<div class="message-icon user-icon">
							<v-icon size="16" color="white">
								fa-user
							</v-icon>
						</div>
					</template>
					<div v-else class="message-body">
						<details
							v-if="m.agentThought"
							class="agent-thought-block"
						>
							<summary class="agent-thought-summary">
								Thought process
							</summary>
							<div class="agent-thought-body message-content">
								<template v-if="isAssistantStreaming(idx)">
									{{ formatMessageDisplay(m.agentThought) }}
								</template>
								<span v-else v-html="formatMessage(m.agentThought)" />
							</div>
						</details>
						<div class="message-bubble message-bubble--assistant-plain">
							<div class="message-content">
								<div
									v-if="isLoading && idx === messages.length - 1 && isAgentDisplayContentEmpty(m.content)"
									class="typing-indicator"
								>
									<span />
									<span />
									<span />
								</div>
								<template v-else-if="isAssistantStreaming(idx)">
									<span
										class="message-content-text message-content--streaming"
									>{{ formatMessageDisplay(m.content) }}</span>
								</template>
								<span v-else v-html="formatMessage(m.content)" />
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="input-area pa-2">
				<input
					ref="fileInput"
					type="file"
					class="wf-file-input"
					multiple
					:accept="chatAttachmentAccept"
					@change="onAttachmentFilesSelected"
				>
				<div
					v-if="pendingAttachments.length"
					class="wf-attachment-chips mb-2"
				>
					<v-chip
						v-for="(a, ai) in pendingAttachments"
						:key="`att-${ai}-${a.filename}`"
						small
						close
						class="mr-1 mb-1"
						@click:close="removePendingAttachment(ai)"
					>
						{{ a.filename }}
					</v-chip>
				</div>
				<div
					v-if="attachmentError"
					class="caption error--text mb-1"
				>
					{{ attachmentError }}
				</div>
				<v-textarea
					v-model="inputMessage"
					auto-grow
					rows="1"
					hide-details
					dense
					outlined
					class="wf-textarea wf-textarea--with-actions"
					:placeholder="voice.isRecording ? 'Recording…' : 'Ask the assistant to update this page…'"
					:disabled="isLoading"
					:readonly="voice.isRecording"
					@keydown.enter.exact.prevent="onEnterSend"
				>
					<template v-slot:append>
						<div class="wf-append-actions">
							<v-tooltip bottom>
								<template #activator="{ on, attrs }">
									<v-btn
										v-bind="attrs"
										class="wf-icon-action"
										icon
										small
										text
										:disabled="isLoading || voice.isRecording"
										v-on="on"
										@click="openAttachmentPicker"
									>
										<v-icon small>
											mdi mdi-paperclip
										</v-icon>
									</v-btn>
								</template>
								<span>Add images, HTML, CSS, or small text files (max {{ maxAttachmentFiles }} files, ~2MB each)</span>
							</v-tooltip>
							<v-tooltip
								v-if="voice.isSupported"
								bottom
							>
								<template #activator="{ on, attrs }">
									<v-btn
										v-bind="attrs"
										class="wf-icon-action"
										icon
										small
										text
										:color="voice.isRecording ? 'error' : undefined"
										:disabled="isLoading"
										v-on="on"
										@click="handleMicClick"
									>
										<v-icon
											v-if="voice.isRecording"
											small
											class="wf-mic-listening"
										>
											mdi mdi-stop-circle
										</v-icon>
										<v-icon
											v-else
											small
										>
											mdi mdi-microphone-outline
										</v-icon>
									</v-btn>
								</template>
								<span>{{
									voice.isRecording
										? 'Tap to stop, or pause to auto-send'
										: 'Tap to speak (auto-stops when you pause)'
								}}</span>
							</v-tooltip>
							<v-tooltip bottom>
								<template #activator="{ on, attrs }">
									<v-btn
										v-bind="attrs"
										class="wf-icon-action wf-send-icon"
										icon
										small
										text
										:disabled="isLoading || (!inputMessage.trim() && !pendingAttachments.length)"
										v-on="on"
										@click="sendMessage"
									>
										<v-icon small>
											mdi mdi-send
										</v-icon>
									</v-btn>
								</template>
								<span>Send</span>
							</v-tooltip>
						</div>
					</template>
				</v-textarea>
				<div
					v-if="voice.error"
					class="caption error--text mt-1"
				>
					{{ voice.error }}
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import {
	streamAgentChatWithToolLoop,
	mapToolsForAgentApi,
	executeWebsitePageToolRequests,
	mergeStepCompleteStreamState,
	normalizeAgentContentToString,
} from '@/utils/agentStream'
import md from '@/utils/markdown'
import { CHAT_ATTACHMENT_MAX_FILES, readFilesAsChatAttachments } from '@/utils/chatAttachments'
import createVoiceRecorderApi from '@/utils/voiceRecorder'
import { AGENT_CHAT_HISTORY_CHAR_CAP } from '@/features/websitePageEditorApi'
import { createWebsitePageAgentTools } from '@/features/websitePageAgentTools'

function truncateAgentContextText(s, maxLen) {
	const t = String(s || '')
	if (t.length <= maxLen) return t
	return `${t.slice(0, maxLen)}…`
}

/** Shown in orchestrator context so the planner issues tools instead of answering in chat only. */
const WEBSITE_PAGE_AGENT_CONTEXT_DESCRIPTION = [
	'Website **page builder** assistant (visual editor iframe).',
	'Whenever the user asks to **add, create, remove, or change** something on the page — including **sections**, **cards**,',
	'**columns**, **grids**, **seasonal** content, **copy on the canvas**, or **CSS** — the tool-orchestration model **must**',
	'invoke **website_page_sync_agent_layout** (and optionally **website_page_get_state** first).',
	'When **adding** a section: call **website_page_get_state**, then include the new `{ id, html, agentCss? }` in `sections` —',
	'**partial lists merge safely** (omitted agent-html blocks are kept; all-new slugs append after existing unless `sectionsReplaceAll`).',
	'Writing HTML or explanations in assistant **chat text does not update the preview**; only those tools apply changes.',
	'Implement layout in **`website_page_sync_agent_layout`**:',
	(
		'pass **`sections` as one JSON string** (array of `{ id, html?, agentCss?, afterSectionId?, i18n? }` '
		+ '— omit `html` / `agentCss` keys to leave those fields unchanged).'
	),
	(
		'Put AI layout styles in **`agentCss`** scoped under **`.agent-html-scope--<id>`**; '
		+ 'reserve **`stylesheet`** for rare global overrides.'
	),
	'Use **`agentSectionsOrder`** (JSON string of ids) for id-only reorder; slug `id` values: lowercase, hyphens,',
	'match /^[a-z][a-z0-9-_]+$/ .',
	'**Website brand colors** appear in **currentState** as `websitePrimaryColor` and `websiteSecondaryColor`',
	'(hub settings) — prefer them in generated CSS unless the user specifies other colors.',
	'Optional **i18n** per section: JSON `fieldId -> { locale: text }`, stored on `page.data[id].i18n`',
	'and merged into **`page.strings`** as `texts.agent-<id>-<fieldId>`; use **`data-i18n-field="<fieldId>"`** in HTML for editable spans.',
	'Use **locale keys** that match the site (see **`websiteLocales`** / **`editorSelectedLocale`** in **currentState**);',
	'For **editable images** use **`<img data-i18n-image="<fieldId>" src="…">`** ',
	'so users can click the image in the builder and upload a replacement (draft slug `agent-<id>-<fieldId>`).',
	(
		'**CTAs:** use **`<a data-i18n-field="<b>" data-i18n-cta href="#">…</a>`** (simple link; i18n **`b`** + **`b-href`**) '
		+ 'or **`<a data-i18n-href-field="<b>" href="#">…</a>`** for a large clickable block with inner **`data-i18n-field`** '
		+ 'children (i18n **`b-href`** required; **`b`** optional). '
		+ 'Do **not** nest `data-i18n-field` inside a `data-i18n-cta` link. '
		+ 'External `http(s)` links get `target="_blank"` in preview.'
	),
].join(' ')

const EXPIRY_BUFFER_MS = 3 * 60 * 1000
/** Max rate for scroll during SSE (orchestrator unaffected). */
const STREAM_UI_THROTTLE_MS = 400
const CHAT_ATTACHMENT_ACCEPT = [
	'image/*',
	'.csv,.json,.md,.txt,.html,.htm,.css',
	'text/plain,text/csv,text/markdown,text/html,text/css',
	'application/json',
].join(',')

function parseTtlToSeconds(ttl) {
	if (!ttl || typeof ttl !== 'string') return 300
	const match = ttl.match(/^(\d+)([smh])$/)
	if (!match) return 300
	const n = parseInt(match[1], 10)
	const unit = match[2]
	if (unit === 's') return n
	if (unit === 'm') return n * 60
	if (unit === 'h') return n * 3600
	return 300
}

export default {
	name: 'WebsitePageAgentAssistant',
	props: {
		ravelloHelper: {
			type: Object,
			required: true,
		},
		/** Returns live editor API from parent (or null if not ready) */
		getEditorApi: {
			type: Function,
			required: true,
		},
		pageId: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			inputMessage: '',
			isLoading: false,
			sessionId: null,
			messages: [],
			cachedToken: null,
			cachedExpiresAt: 0,
			/** Pending uploads for the next send (base64 payload built in readFilesAsChatAttachments) */
			pendingAttachments: [],
			attachmentError: null,
			/** Voice UI + recorder state (trufront-style); isSupported set in createVoiceRecorderApi */
			voice: {
				isRecording: false,
				error: null,
				isSupported: false,
			},
			voiceApi: null,
		}
	},
	computed: {
		maxAttachmentFiles() {
			return CHAT_ATTACHMENT_MAX_FILES
		},
		chatAttachmentAccept() {
			return CHAT_ATTACHMENT_ACCEPT
		},
		agentBaseUrl() {
			const u = (typeof window !== 'undefined' && window.appConfigs && window.appConfigs.AGENT_ORCHESTRATOR_URL) || ''
			return String(u).replace(/\/$/, '')
		},
		/** Public folder assets (same filenames as @trubase/trufront/public). */
		publicAssetBase() {
			const base = process.env.BASE_URL || '/'
			return base.endsWith('/') ? base : `${base}/`
		},
		logoWaifinderSrc() {
			return `${this.publicAssetBase}logo_waifinder.svg`
		},
		waifinderSymbolSrc() {
			return `${this.publicAssetBase}waifinder_symbol.png`
		},
		storageKeySession() {
			return `ravello-admin/websitePageAgent/session/${this.pageId}`
		},
		storageKeyMessages() {
			return `ravello-admin/websitePageAgent/messages/${this.pageId}`
		},
	},
	watch: {
		pageId() {
			this.startFreshSessionWithIntro()
		},
	},
	mounted() {
		this.voiceApi = createVoiceRecorderApi(this.voice)
		this.startFreshSessionWithIntro()
	},
	beforeDestroy() {
		if (this.voiceApi) {
			this.voiceApi.dispose()
			this.voiceApi = null
		}
	},
	methods: {
		clearStoredAgentChat() {
			try {
				localStorage.removeItem(this.storageKeySession)
				localStorage.removeItem(this.storageKeyMessages)
			} catch (e) {
				// ignore
			}
		},
		/**
		 * New session on first mount for this page, when `pageId` changes, or manual reset —
		 * not when switching leftbar tabs (see EditorLeftbar v-show).
		 */
		startFreshSessionWithIntro() {
			if (this.voiceApi) this.voiceApi.reset()
			if (this.voice) this.voice.error = null
			this.sessionId = null
			this.messages = []
			this.cachedToken = null
			this.cachedExpiresAt = 0
			this.pendingAttachments = []
			this.attachmentError = null
			this.clearStoredAgentChat()
			this.messages.push({
				role: 'assistant',
				content: 'What do you want to achieve today?',
				agentThought: '',
				timestamp: new Date(),
			})
		},
		resetChat() {
			if (this.voiceApi) this.voiceApi.reset()
			if (this.voice) this.voice.error = null
			this.sessionId = null
			this.messages = []
			this.cachedToken = null
			this.cachedExpiresAt = 0
			this.pendingAttachments = []
			this.attachmentError = null
			this.clearStoredAgentChat()
			this.messages.push({
				role: 'assistant',
				content: 'Chat reset. How can I help with this page?',
				agentThought: '',
				timestamp: new Date(),
			})
		},
		async getAgentSessionToken(opts = {}) {
			const { forceRefresh = false } = opts || {}
			try {
				if (!forceRefresh && this.cachedToken && Date.now() < this.cachedExpiresAt - EXPIRY_BUFFER_MS) {
					return this.cachedToken
				}
				const data = await this.ravelloHelper.request().v(2).to('agent').to('session-token')
					.create({})
				const t = data && data.token
				const ttl = (data && data.expiresIn) || '5m'
				if (t) {
					this.cachedToken = t
					this.cachedExpiresAt = Date.now() + (parseTtlToSeconds(ttl) * 1000)
				} else {
					this.cachedToken = null
					this.cachedExpiresAt = 0
				}
				return this.cachedToken
			} catch (e) {
				// eslint-disable-next-line no-console
				console.warn('Agent session token failed:', (e && e.response && e.response.data) || e.message || e)
				this.cachedToken = null
				this.cachedExpiresAt = 0
				return null
			}
		},
		onEnterSend() {
			if ((!this.inputMessage.trim() && !this.pendingAttachments.length) || this.voice.isRecording) return
			this.sendMessage()
		},
		openAttachmentPicker() {
			const el = this.$refs.fileInput
			if (el) el.click()
		},
		removePendingAttachment(index) {
			this.pendingAttachments.splice(index, 1)
		},
		async onAttachmentFilesSelected(e) {
			const { files } = e.target
			this.attachmentError = null
			if (!files || !files.length) {
				e.target.value = ''
				return
			}
			try {
				const newOnes = await readFilesAsChatAttachments(files)
				const room = CHAT_ATTACHMENT_MAX_FILES - this.pendingAttachments.length
				if (room <= 0) {
					this.attachmentError = `You can attach at most ${CHAT_ATTACHMENT_MAX_FILES} files.`
				} else {
					const slice = newOnes.slice(0, room)
					this.pendingAttachments = [...this.pendingAttachments, ...slice]
					if (newOnes.length > room) {
						this.attachmentError = `Only ${room} more file(s) added (max ${CHAT_ATTACHMENT_MAX_FILES}).`
					}
				}
			} catch (err) {
				this.attachmentError = (err && err.message) || 'Could not read file(s).'
			}
			e.target.value = ''
		},
		async handleMicClick() {
			if (!this.voiceApi || !this.agentBaseUrl) return
			this.voice.error = null
			if (this.voice.isRecording) {
				const blob = await this.voiceApi.stop()
				await this.sendAudioBlob(blob)
			} else {
				this.voiceApi.start({
					onAutoStop: (blob) => {
						this.sendAudioBlob(blob)
					},
					silenceMs: 1200,
				})
			}
		},
		async sendAudioBlob(blob) {
			if (!blob || this.isLoading || !this.agentBaseUrl || !this.voiceApi) return
			const base64 = await this.voiceApi.blobToBase64(blob)
			const mimeType = (blob.type && blob.type.split(';')[0]) || 'audio/webm'
			await this.processAgentMessage('', {
				audio: base64,
				audioMimeType: mimeType,
			})
		},
		isAgentDisplayContentEmpty(content) {
			return !normalizeAgentContentToString(content).trim()
		},
		isAssistantStreaming(idx) {
			return this.isLoading && idx === this.messages.length - 1
		},
		/** Plain text while SSE is in flight; markdown once the turn settles. */
		formatMessageDisplay(text) {
			if (text == null) return ''
			return normalizeAgentContentToString(text).trimEnd()
		},
		formatMessage(content) {
			const text = this.formatMessageDisplay(content)
			if (!text) return ''
			let html = md.toHtml(text)
			html = html.replace(/<a href=/g, '<a target="_blank" rel="noopener noreferrer" href=')
			return html
		},
		scrollToBottom() {
			this.$nextTick(() => {
				const el = this.$refs.messagesEl
				if (el) {
					el.scrollTop = el.scrollHeight
				}
			})
		},
		sendMessage() {
			const userMessage = this.inputMessage.trim()
			const attachments = this.pendingAttachments.slice()
			if ((!userMessage && !attachments.length) || this.isLoading || !this.agentBaseUrl || this.voice.isRecording) return
			this.inputMessage = ''
			this.pendingAttachments = []
			this.attachmentError = null
			this.processAgentMessage(userMessage, { attachments })
		},
		async processAgentMessage(userMessage, voiceOpts = {}) {
			const { audio, audioMimeType, attachments: attachmentPayload = [] } = voiceOpts
			const isVoiceMessage = !!audio
			if (this.isLoading || !this.agentBaseUrl) return
			if (!isVoiceMessage && !(String(userMessage || '').trim()) && !attachmentPayload.length) return

			let displayUserText = isVoiceMessage ? 'Transcribing…' : String(userMessage || '').trim()
			if (!isVoiceMessage && attachmentPayload.length) {
				const names = attachmentPayload.map(a => a.filename).join(', ')
				displayUserText = displayUserText
					? `${displayUserText}\n(Attached: ${names})`
					: `(Attached: ${names})`
			}
			this.messages.push({
				role: 'user',
				content: displayUserText,
				timestamp: new Date(),
			})
			const userMessageIndex = this.messages.length - 1

			this.isLoading = true
			this.scrollToBottom()

			const tools = createWebsitePageAgentTools(this.getEditorApi)
			const assistantIndex = this.messages.length
			this.messages.push({
				role: 'assistant',
				content: '',
				agentThought: '',
				timestamp: new Date(),
			})

			let editorSnapshot = {}
			try {
				const api = typeof this.getEditorApi === 'function' ? this.getEditorApi() : null
				if (api && typeof api.getSnapshot === 'function') {
					editorSnapshot = api.getSnapshot({ compact: true })
				}
			} catch (_e) {
				// keep empty snapshot if editor not ready
			}

			const conversationHistory = (() => {
				const hist = this.messages.slice(0, -1).slice(-10)
				const lastIdx = hist.length - 1
				return hist.map((m, idx) => ({
					role: m.role,
					content: idx === lastIdx
						? m.content
						: truncateAgentContextText(m.content, AGENT_CHAT_HISTORY_CHAR_CAP),
				}))
			})()

			const sharedContext = {
				app: 'ravello-admin',
				currentPage: {
					path: `ravello-admin/website/page/${this.pageId}`,
					description: WEBSITE_PAGE_AGENT_CONTEXT_DESCRIPTION,
				},
				currentState: editorSnapshot,
				availableTools: mapToolsForAgentApi(tools),
				conversationHistory,
			}

			const requestBody = isVoiceMessage
				? {
					audio,
					threadId: this.sessionId || undefined,
					context: {
						...sharedContext,
						audioMimeType,
					},
				}
				: {
					message: String(userMessage || '').trim(),
					threadId: this.sessionId || undefined,
					context: {
						...sharedContext,
						...(attachmentPayload.length ? { chatAttachments: attachmentPayload } : {}),
					},
				}

			let streamLastScroll = 0
			let turnHadFrontendTools = false

			try {
				const { accumulatedContent, thoughtContent } = await streamAgentChatWithToolLoop({
					agentBaseUrl: this.agentBaseUrl,
					getThreadId: () => this.sessionId,
					initialBody: requestBody,
					getToken: o => this.getAgentSessionToken(o),
					onStreamUpdate: (u) => {
						const msg = this.messages[assistantIndex]
						if (!msg) return
						if (u.thought !== undefined) msg.agentThought = u.thought
						if (u.content !== undefined && !turnHadFrontendTools) {
							msg.content = u.content
						}
						const now = Date.now()
						if (now - streamLastScroll >= STREAM_UI_THROTTLE_MS) {
							streamLastScroll = now
							this.scrollToBottom()
						}
					},
					onThreadCreated: (id) => {
						this.sessionId = id
					},
					onTranscript: (transcript) => {
						if (!isVoiceMessage) return
						const um = this.messages[userMessageIndex]
						if (um && um.role === 'user') {
							um.content = (transcript && String(transcript).trim()) || '(no speech detected)'
						}
					},
					onStepComplete: (chunk, streamState) => mergeStepCompleteStreamState(chunk, streamState),
					executeToolRequests: async (toolRequest) => {
						const msg = this.messages[assistantIndex]
						return executeWebsitePageToolRequests(toolRequest.requests, {
							tools,
							onToolStart: () => {
								turnHadFrontendTools = true
								if (msg) msg.content = ''
							},
							onToolComplete: (tool, success, observation) => {
								if (!success && msg) {
									const hint = (observation && observation.error) || `Tool "${tool.name}" failed`
									msg.content = `${msg.content || ''}\n\n_(Page update failed: ${hint})_`.trim()
								}
							},
						})
					},
				})

				const msg = this.messages[assistantIndex]
				if (msg) {
					msg.content = accumulatedContent
					if (thoughtContent) msg.agentThought = thoughtContent
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.error('Website page agent error:', err)
				const msg = this.messages[assistantIndex]
				if (msg) {
					msg.content = 'Sorry, something went wrong. Check the agent URL, hub session-token route, and browser console.'
				}
			} finally {
				this.isLoading = false
				this.scrollToBottom()
			}
		},
	},
}
</script>

<style scoped>
/* WayFinder / Trufront agent palette (TrubaseAgentPane + default.vue scrollbar) */
.website-page-agent-assistant {
	--wf-primary: #f57f1d;
	--wf-primary-hover: #ec7b20;
	--wf-dark: #101330;
	--wf-user-bubble: #424242;
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
	background: #fff;
	direction: ltr;
	text-align: left;
}

.pane-header {
	background-color: #fff;
	color: var(--wf-dark);
	padding: 8px 12px;
	flex-shrink: 0;
	border-bottom: 1px solid #e0e0e0;
}

.header-content {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	gap: 8px;
}

.header-left {
	display: flex;
	align-items: center;
	min-height: 0;
	flex: 1 1 auto;
	min-width: 0;
}

.header-logo {
	height: 28px;
	width: auto;
	max-width: min(200px, calc(100% - 40px));
	display: block;
	object-fit: contain;
	object-position: left center;
}

.header-right {
	display: flex;
	align-items: center;
	flex-shrink: 0;
	gap: 4px;
}

.header-action-btn ::v-deep .v-icon {
	color: var(--wf-dark);
	opacity: 0.75;
}

.messages-container {
	flex: 1 1 auto;
	overflow-y: auto;
	min-height: 120px;
	max-height: calc(100vh - 280px);
	padding: 8px 4px 12px;
	background-color: #fff;
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.message-wrapper {
	display: flex;
	width: 100%;
	align-items: flex-start;
	gap: 6px;
}

.user-message {
	justify-content: flex-end;
}

.bot-message {
	justify-content: flex-start;
}

.message-icon {
	width: 32px;
	height: 32px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.wayfinder-symbol {
	display: block;
	object-fit: contain;
	object-position: center;
}

.wayfinder-symbol--message {
	width: 28px;
	height: 28px;
}

.user-icon {
	background-color: var(--wf-user-bubble);
	color: #fff;
}

.message-body {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.message-bubble {
	flex: 0 1 auto;
	align-self: flex-start;
	width: max-content;
	max-width: 75%;
	min-width: 0;
	box-sizing: border-box;
	padding: 8px 12px 7px;
	border-radius: 16px;
	word-wrap: break-word;
	overflow-wrap: anywhere;
	text-align: left;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.user-message .message-bubble {
	background-color: var(--wf-user-bubble);
	color: #fff;
	border-radius: 18px 18px 4px 18px;
}

.bot-message .message-bubble--assistant-plain {
	background-color: transparent;
	color: var(--wf-dark);
	box-shadow: none;
	padding: 4px 0 8px;
	border-radius: 0;
	max-width: 100%;
	width: 100%;
}

.agent-thought-block {
	margin: 0 0 6px;
	padding: 0;
	border-radius: 8px;
	background: rgba(16, 19, 48, 0.035);
	border: 1px solid rgba(16, 19, 48, 0.06);
	text-align: left;
	min-width: 0;
	max-width: 100%;
}

.agent-thought-summary {
	display: block;
	cursor: pointer;
	font-size: 0.72rem;
	font-weight: 500;
	letter-spacing: 0.02em;
	color: rgba(16, 19, 48, 0.45);
	padding: 6px 8px;
	list-style: none;
	user-select: none;
	text-align: left;
}

.agent-thought-summary::-webkit-details-marker {
	display: none;
}

.agent-thought-body {
	padding: 0 8px 6px;
	font-size: 0.72rem;
	line-height: 1.38;
	color: rgba(16, 19, 48, 0.5);
	text-align: left;
	max-height: min(26vh, 168px);
	overflow-y: auto;
}

.agent-thought-body ::v-deep p {
	margin: 0 0 4px 0;
}

.agent-thought-body ::v-deep p:last-child {
	margin-bottom: 0;
}

.message-body > .agent-thought-block ~ .message-bubble--assistant-plain .message-content {
	margin-top: 2px;
	padding-top: 14px;
	border-top: 1px solid rgba(16, 19, 48, 0.18);
	font-size: 0.98rem;
	line-height: 1.62;
}

.message-content {
	font-size: 0.95rem;
	line-height: 1.6;
	text-align: left;
	min-width: 0;
}

.message-content ::v-deep p {
	margin: 0 0 8px 0;
}

.message-content ::v-deep p:last-child {
	margin-bottom: 0;
}

.message-content ::v-deep code {
	background-color: rgba(0, 0, 0, 0.05);
	padding: 2px 4px;
	border-radius: 3px;
	font-family: monospace;
	font-size: 0.9em;
}

.message-content ::v-deep pre {
	background-color: rgba(0, 0, 0, 0.05);
	padding: 12px;
	border-radius: 4px;
	overflow-x: auto;
	margin: 8px 0;
}

.message-content ::v-deep pre code {
	background-color: transparent;
	padding: 0;
}

.message-content ::v-deep ul,
.message-content ::v-deep ol {
	margin: 8px 0;
	padding-left: 24px;
}

.message-content ::v-deep li {
	margin: 4px 0;
}

.message-content ::v-deep strong {
	font-weight: 600;
}

.message-content ::v-deep em {
	font-style: italic;
}

.message-content ::v-deep blockquote {
	border-left: 3px solid rgba(0, 0, 0, 0.1);
	padding-left: 12px;
	margin: 8px 0;
	color: rgba(0, 0, 0, 0.7);
}

.message-content ::v-deep a {
	color: var(--wf-primary);
	text-decoration: underline;
}

.user-message .message-content ::v-deep code {
	background-color: rgba(255, 255, 255, 0.2);
}

.user-message .message-content ::v-deep pre {
	background-color: rgba(255, 255, 255, 0.15);
}

.user-message .message-content ::v-deep blockquote {
	border-left-color: rgba(255, 255, 255, 0.3);
	color: rgba(255, 255, 255, 0.9);
}

.user-message .message-content ::v-deep a {
	color: rgba(255, 255, 255, 0.9);
}

.message-content-text {
	display: block;
	text-align: left;
}

.message-content--streaming {
	white-space: pre-wrap;
}

.typing-indicator {
	display: flex;
	gap: 4px;
	padding: 4px 0;
}

.typing-indicator span {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: #404040;
	animation: wf-typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
	animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
	animation-delay: 0.4s;
}

@keyframes wf-typing {
	0%,
	60%,
	100% {
		transform: translateY(0);
		opacity: 0.35;
	}
	30% {
		transform: translateY(-4px);
		opacity: 1;
	}
}

.input-area {
	flex: 0 0 auto;
	border-top: 1px solid #e0e0e0;
	background: #fff;
}

.wf-file-input {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;
	overflow: hidden;
}

.wf-attachment-chips {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
}

/* Icons live in v-textarea append slot (inside outline) — row layout, bottom-aligned like trufront */
.wf-textarea--with-actions ::v-deep .v-input__slot {
	align-items: flex-end;
}

.wf-textarea--with-actions ::v-deep .v-input__append-inner {
	margin-top: 0 !important;
	padding-bottom: 6px;
	padding-left: 4px;
	align-self: flex-end;
}

.wf-append-actions {
	display: inline-flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	flex-wrap: nowrap;
	gap: 2px;
	min-height: 36px;
}

.wf-icon-action ::v-deep .v-icon {
	color: rgba(16, 19, 48, 0.55);
}

.wf-send-icon:not(:disabled):hover ::v-deep .v-icon {
	color: var(--wf-primary);
}

.wf-mic-listening {
	animation: wf-mic-pulse 1.2s ease-in-out infinite;
}

@keyframes wf-mic-pulse {
	0%,
	100% {
		opacity: 1;
	}
	50% {
		opacity: 0.45;
	}
}

.wf-textarea ::v-deep .v-input--is-focused fieldset {
	border-color: var(--wf-primary) !important;
}
</style>
